import { useQuery } from '@tanstack/react-query';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import patientService from '../../services/patientService';
import type { PatientPlanRecord } from '../../types/patientPlan.types';
import { parsePatientId } from './usePatientBasicInfo';

export interface PatientInsuranceData {
  insurance: PatientPlanRecord[];
  plans: PatientPlanRecord[];
}

/**
 * Hook to fetch patient insurance/plans from the PMS (Kroll or Fillware).
 * Only fetches when the Insurance tab is active.
 *
 * PRX-33: `GET /patients/mqtt/:pharmacyId/:patientId/plans` answers a BARE
 * ARRAY of plans (`PatientPlanDto[]`). This hook read `response.plans`, which
 * is undefined on an array, so the Insurance tab said "No insurance plans
 * found" for every patient — Kroll and Fillware alike — while the PMS query
 * was returning rows. A non-array answer is a contract break and is thrown,
 * never rendered as "no plans".
 *
 * PRX-80 W5: so is a plan without a boolean `inactive` flag. The tab files a
 * plan under Current or Previous by that flag alone, so a missing one (a
 * frontend deployed ahead of the backend, a reshaped response) would read as
 * a current, green Active plan.
 */
export function usePatientInsurance(
  patientId: string | undefined,
  options?: { enabled?: boolean }
) {
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);
  const { pharmacyId, patientNumber } = parsePatientId(patientId, activePharmacy?.id);

  const enabled = options?.enabled !== false && !!pharmacyId && !!patientNumber;

  return useQuery({
    queryKey: ['patient', 'insurance', pharmacyId, patientNumber],
    queryFn: async (): Promise<PatientInsuranceData> => {
      if (!pharmacyId || !patientNumber) {
        throw new Error('Invalid patient ID');
      }

      const response = await patientService.getPatientPlansFromKroll(pharmacyId, patientNumber);
      if (!Array.isArray(response)) {
        throw new Error(
          `Unexpected insurance plans response for patient ${patientNumber}: expected an array of plans`,
        );
      }
      if (response.some((plan) => typeof plan?.inactive !== 'boolean')) {
        throw new Error(
          `Unexpected insurance plans response for patient ${patientNumber}: a plan has no inactive flag`,
        );
      }

      return {
        insurance: response,
        plans: response,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - insurance doesn't change often
  });
}
